import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  JustifiBalanceTransaction,
  JustifiTransactionsSummary,
  Processor,
  StripeTransactionItem,
  StripeTransactionsSummary,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { formatDate } from '@frontend/date';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { downloadCSV } from '@frontend/media-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { ExportIcon, Heading, IconButton, Table, useAlert } from '@frontend/design-system';
import { useCanDoActionsMulti } from '../../../hooks/use-can-do-actions-multi';
import { useGetPayoutTransactionsV1Query } from '../../../hooks/use-get-payout';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { prettify } from '../../../utils/date-helpers';
import { setAndNotUnknown } from '../../../utils/helpers';
import { DetailCard } from '../../Card/detail-card';
import { generateStripeColumns, generateJustifiColumns } from './generate-columns';

export const StripePayoutTransactionsTable = ({
  payoutId,
  stripeTransactionsSummary,
  isLoading,
}: {
  payoutId: string;
  stripeTransactionsSummary?: StripeTransactionsSummary;
  isLoading: boolean;
}) => {
  const alert = useAlert();
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();

  const { canExport } = useCanDoActionsMulti(selectedLocationIds, 'any');

  const columns = useMemo(() => generateStripeColumns(t), []);
  const transactions = stripeTransactionsSummary?.transaction?.transactionItems || [];
  // only show payouts, hide fees if necessary
  const displayedTransactions = transactions.reduce<Partial<StripeTransactionItem>[]>(
    (acc, { transactionType, fee, patientPmid = '', personName = '', transactionDate, amount, ...rest }) =>
      transactionType?.toLowerCase() !== 'payout' && transactionType?.toLowerCase() !== 'unknown_transaction_type'
        ? [
            ...acc,
            {
              transactionType,
              fee,
              amount,
              patientPmid,
              personName,
              transactionDate,
              ...rest,
            },
          ]
        : acc,
    []
  );

  const transformDataForCsvExport = (data: Partial<StripeTransactionItem>[] = []) => {
    return data.map((transaction) => {
      const type = prettify(transaction.transactionType ?? '', { split: '_' });
      const net = formatCentsToCurrency(transaction.net)?.replace(',', '');
      const gross = formatCentsToCurrency(transaction.amount)?.replace(',', '');
      const fees = formatCentsToCurrency(transaction.fee)?.replace(',', '');
      const confirmationCode = transaction.confirmationCode;
      const txDate = formatDate(transaction.created, 'YYYY-MM-D');
      const etaDate = formatDate(transaction.arrivalTime, 'YYYY-MM-D');
      const personName = setAndNotUnknown(transaction.personName) ? transaction.personName?.replace(',', ' ') : '-';

      return {
        Type: type,
        'Person Name': personName,
        'Patient ID': setAndNotUnknown(transaction.patientPmid, 3) ? transaction.patientPmid : '',
        Gross: gross,
        Fees: fees,
        Total: net,
        'Transaction Date': txDate,
        'Confirmation Code': confirmationCode,
        'Payout Date': etaDate,
      };
    });
  };

  const exportTransactions = () => {
    if (!canExport) {
      alert.error(t('Payment admin role required'));
      return;
    }

    if (stripeTransactionsSummary && transactions && transactions.length > 0) {
      const filename = `payout_${payoutId}`;
      const transformedData = transformDataForCsvExport(displayedTransactions);

      // @ts-ignore Complaining about data being potentially undefined
      downloadCSV(transformedData, filename);
    }
  };

  return (
    <DetailCard>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <Heading level={2}>{t('Transactions')}</Heading>
        {transactions && (
          <IconButton
            onClick={exportTransactions}
            label={t('Export')}
            showLabelOnHover={true}
            data-trackingid='pay-portal-payoutdetails-export'
          >
            <ExportIcon />
          </IconButton>
        )}
      </div>
      <Divider />
      <Table
        colConfig={columns}
        data={displayedTransactions}
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        manualSortBy
        manualFilters
        isLoading={isLoading}
        hasResponsiveColWidths
      />
    </DetailCard>
  );
};

export const JustifiPayoutTransactionsTable = ({ payoutId, locationId }: { payoutId: string; locationId: string }) => {
  const alert = useAlert();
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const { data, isLoading } = useGetPayoutTransactionsV1Query({
    payoutId,
    locationId,
    processor: Processor.JUSTIFI,
  });

  const justifiTransactionsSummary: JustifiTransactionsSummary | undefined =
    data && 'justifiTransactionsSummary' in data && data?.justifiTransactionsSummary
      ? data?.justifiTransactionsSummary
      : undefined;

  const { canExport } = useCanDoActionsMulti(selectedLocationIds, 'any');

  const columns = useMemo(() => generateJustifiColumns(t), []);
  const transactions = justifiTransactionsSummary?.balanceTransactions || [];
  // only show payouts, hide fees if necessary
  const displayedTransactions = transactions.reduce<Partial<JustifiBalanceTransaction>[]>(
    (acc, { txnType, fee, createdTime, amount, ...rest }) =>
      txnType?.toLowerCase() !== 'payout' && txnType?.toLowerCase() !== 'unknown_transaction_type'
        ? [
            ...acc,
            {
              txnType,
              fee,
              amount,
              createdTime,
              ...rest,
            },
          ]
        : acc,
    []
  );

  const transformDataForCsvExport = (data: Partial<JustifiBalanceTransaction>[] = []) => {
    return data.map((transaction) => {
      const type = prettify(transaction.txnType ?? '', { split: '_' });
      const net = formatCentsToCurrency(transaction.net)?.replace(',', '');
      const gross = formatCentsToCurrency(transaction.amount)?.replace(',', '');
      const fees = formatCentsToCurrency(transaction.fee)?.replace(',', '');
      const id = transaction.id;
      const txDate = formatDate(transaction.createdTime, 'YYYY-MM-D');

      return {
        Type: type,
        Gross: gross,
        Fees: fees,
        Total: net,
        'Transaction Date': txDate,
        'Confirmation Code': id,
      };
    });
  };

  const exportTransactions = () => {
    if (!canExport) {
      alert.error(t('Payment admin role required'));
      return;
    }

    if (data && transactions && transactions.length > 0) {
      const filename = `payout_${payoutId}`;
      const transformedData = transformDataForCsvExport(displayedTransactions);

      // @ts-ignore Complaining about data being potentially undefined
      downloadCSV(transformedData, filename);
    }
  };

  return (
    <DetailCard>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <Heading level={2}>{t('Transactions')}</Heading>
        {transactions && (
          <IconButton
            onClick={exportTransactions}
            label={t('Export')}
            showLabelOnHover={true}
            data-trackingid='pay-portal-payoutdetails-export'
          >
            <ExportIcon />
          </IconButton>
        )}
      </div>
      <Divider />
      <Table
        colConfig={columns}
        data={displayedTransactions}
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        manualSortBy
        manualFilters
        isLoading={isLoading}
        hasResponsiveColWidths
      />
    </DetailCard>
  );
};
